/**
 * A mecânica da fila da galeria, sem React.
 *
 * Está aqui fora por dois motivos. O primeiro é que ordem embaralhada erra em
 * silêncio: uma foto some do ciclo, outra repete na virada, e ninguém percebe
 * olhando a TV. O segundo é que `apps/**\/*.test.ts` já roda no Vitest — um
 * módulo puro é a única parte do frontend que dá para testar hoje.
 *
 * Todo sorteio recebe `aleatorio` para que o teste seja determinístico.
 */

export interface Slide {
  chave: string;
  url: string;
  /**
   * O arquivo original, para quando a derivada de exibição não estiver lá.
   *
   * A projeção roda sozinha numa TV durante o evento: uma foto que não carrega
   * some do ciclo sem que ninguém perceba, e é justamente a foto de alguém que
   * está na sala esperando se ver.
   */
  urlOriginal?: string;
  legenda?: string;
  tipo: "image" | "video";
  segundos: number;
}

export interface EstadoFila {
  /** O ciclo atual, na ordem de exibição. */
  fila: Slide[];
  indice: number;
  /**
   * Conta avanços desde que a projeção começou, e nunca volta. É o que a `key`
   * do elemento e o cronômetro observam — sem ele, um ciclo de um item só
   * devolve sempre a mesma referência, o efeito não re-executa e a projeção
   * congela (com vídeo, no último quadro).
   */
  passo: number;
  /** Chave do último slide exibido, para não repetir na virada do ciclo. */
  ultima?: string;
  /** Em qual divulgação a rotação parou, para o ciclo seguinte continuar dela. */
  proximaDiv: number;
}

export type Sorteio = () => number;

export const FILA_VAZIA: EstadoFila = { fila: [], indice: 0, passo: 0, proximaDiv: 0 };

/** Fisher-Yates. O `sort(() => Math.random() - 0.5)` do atalho é enviesado. */
export function embaralhar<T>(itens: readonly T[], aleatorio: Sorteio = Math.random): T[] {
  const r = [...itens];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(aleatorio() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}

/**
 * Monta um ciclo: as fotos embaralhadas, com divulgação a cada N.
 *
 * As divulgações não são embaralhadas — a ordem delas é conteúdo, definida pelo
 * operador em Divulgações da vivência. Elas só giram, e a rotação atravessa a
 * virada do ciclo para que a terceira peça não fique sem nunca aparecer quando
 * o ciclo só tem dois espaços.
 */
export function montarCiclo(
  fotos: readonly Slide[],
  divulgacoes: readonly Slide[],
  aCada: number,
  opcoes: { ultima?: string; proximaDiv?: number; aleatorio?: Sorteio } = {},
): { fila: Slide[]; proximaDiv: number } {
  const { ultima, proximaDiv = 0, aleatorio = Math.random } = opcoes;
  const intervalo = Math.max(1, Math.floor(aCada));

  const f = embaralhar(fotos, aleatorio);
  if (ultima !== undefined && f.length > 1 && f[0]!.chave === ultima) {
    [f[0], f[1]] = [f[1]!, f[0]!];
  }

  if (divulgacoes.length === 0) return { fila: f, proximaDiv };
  if (f.length === 0) return { fila: [...divulgacoes], proximaDiv };

  const fila: Slide[] = [];
  let div = proximaDiv;
  f.forEach((slide, i) => {
    fila.push(slide);
    if ((i + 1) % intervalo === 0) {
      fila.push(divulgacoes[div % divulgacoes.length]!);
      div += 1;
    }
  });

  return { fila, proximaDiv: div % divulgacoes.length };
}

/** Um passo adiante; no fim do ciclo, monta o próximo. */
export function avancar(
  estado: EstadoFila,
  fotos: readonly Slide[],
  divulgacoes: readonly Slide[],
  aCada: number,
  aleatorio: Sorteio = Math.random,
): EstadoFila {
  const atual = estado.fila[estado.indice];
  const passo = estado.passo + 1;
  const seguinte = estado.indice + 1;

  if (seguinte < estado.fila.length) {
    return { ...estado, indice: seguinte, passo, ultima: atual?.chave };
  }

  const { fila, proximaDiv } = montarCiclo(fotos, divulgacoes, aCada, {
    ultima: atual?.chave,
    proximaDiv: estado.proximaDiv,
    aleatorio,
  });

  return { fila, indice: 0, passo, ultima: atual?.chave, proximaDiv };
}

/**
 * Reconcilia a fila com o que o servidor devolveu no último polling.
 *
 * Foto nova entra em posição sorteada **do trecho que ainda não passou** —
 * aparece logo, sem ser sempre no fim, e sem furar a vez de quem já estava na
 * frente. Foto removida sai na hora. Divulgação nova segue a mesma regra: a
 * cadência fica um pouco fora do lugar até a virada do ciclo, o que é melhor do
 * que a peça só entrar quando o ciclo acabar.
 *
 * Devolve o **mesmo objeto** quando nada mudou. Isso não é economia: um estado
 * novo a cada 20 s reiniciaria o cronômetro do slide em exibição.
 */
export function sincronizar(
  estado: EstadoFila,
  fotos: readonly Slide[],
  divulgacoes: readonly Slide[],
  aCada: number,
  aleatorio: Sorteio = Math.random,
): EstadoFila {
  const fonte = [...fotos, ...divulgacoes];

  if (fonte.length === 0) {
    return estado.fila.length === 0 ? estado : { ...estado, fila: [], indice: 0, proximaDiv: 0 };
  }

  if (estado.fila.length === 0) {
    const { fila, proximaDiv } = montarCiclo(fotos, divulgacoes, aCada, {
      ultima: estado.ultima,
      aleatorio,
    });
    return { ...estado, fila, indice: 0, proximaDiv };
  }

  const porChave = new Map(fonte.map((s) => [s.chave, s]));

  // Mantidos na ordem em que estavam, já com url e duração atualizadas — a URL
  // é assinada e expira, e a duração pode ter sido editada no acervo.
  const fila: Slide[] = [];
  let indice = estado.indice;
  estado.fila.forEach((s, i) => {
    const vivo = porChave.get(s.chave);
    if (!vivo) {
      if (i < estado.indice) indice -= 1;
      return;
    }

    // O que está na tela conserva a URL com que foi carregado. Trocar o `src`
    // de um `<video>` faz o navegador recarregar o arquivo e voltar ao início:
    // um vídeo mais longo que o intervalo de polling nunca chegaria ao fim, e o
    // `ended` — que é quem manda o avanço — nunca viria. Vale para a imagem
    // também, que piscaria ao recarregar.
    const candidato = i === estado.indice ? { ...vivo, url: s.url } : vivo;

    // Reusa o objeto antigo quando nada mudou de fato. A identidade é o que o
    // cronômetro observa; trocá-la a cada polling reiniciaria a contagem.
    fila.push(mesmoSlide(candidato, s) ? s : candidato);
  });

  // Trocou o conteúdo inteiro — vivência trocada, acervo limpo — não há trecho
  // a preservar: começa um ciclo novo.
  if (fila.length === 0) {
    const novo = montarCiclo(fotos, divulgacoes, aCada, { ultima: estado.ultima, aleatorio });
    return { ...estado, fila: novo.fila, indice: 0, proximaDiv: novo.proximaDiv };
  }

  const presentes = new Set(fila.map((s) => s.chave));
  for (const s of fonte) {
    if (presentes.has(s.chave)) continue;
    const restante = fila.length - indice;
    const pos = restante > 0 ? indice + 1 + Math.floor(aleatorio() * restante) : fila.length;
    fila.splice(pos, 0, s);
  }

  if (indice >= fila.length) indice = 0;
  if (indice < 0) indice = 0;

  const igual =
    indice === estado.indice &&
    fila.length === estado.fila.length &&
    fila.every((s, i) => mesmoSlide(s, estado.fila[i]!));

  return igual ? estado : { ...estado, fila, indice };
}

function mesmoSlide(a: Slide, b: Slide): boolean {
  return (
    a.chave === b.chave &&
    a.url === b.url &&
    a.tipo === b.tipo &&
    a.segundos === b.segundos &&
    a.legenda === b.legenda
  );
}
